import { ActionIcon } from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { CellContext, flexRender } from '@tanstack/react-table';
import { TriangleDown } from 'baseui/icon';
import { KIND, Tag } from 'baseui/tag';
import { PLACEMENT, StatefulTooltip } from 'baseui/tooltip';
import { TrashSVG } from 'common/assets';
import stylesColors from 'common/styles/styles.colors';
import { fontFamily } from 'common/styles/styles.typography';
import { themedStyled } from 'common/styles/styletron.theme';
import { toSentenceCase, toTitleCase } from 'common/utils/string';
import { Action } from 'components/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import NumberFormat from 'react-number-format';

import TableDateInput from './date-input';
import TableSelectInput from './select';
import IndeterminateCheckbox from './table-checkbox';
import { ColumnDataType } from './type';
import { FontCase, FontCaseType } from '../fields/text-field';

interface Props {
  context: CellContext<any, unknown>;
  dispatch: React.Dispatch<any>;
  columnIdx: number;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement>,
    rowIdx: number,
    columnIdx: number,
  ) => void;
  onFocus?: (
    e: React.FocusEvent<HTMLInputElement>,
    rowIdx: number,
    columnIdx: number,
  ) => void;
  disabled?: boolean;
  cell?: any;
  minNumber?: number;
  maxNumber?: number;
}

const TableCell = React.forwardRef<any, Props>((props, ref) => {
  const {
    dispatch,
    onBlur,
    onFocus,
    columnIdx,
    disabled,
    cell: _cell,
    maxNumber,
    minNumber,
  } = props;

  const { t } = useTranslation();
  const { getValue, cell, row } = props.context;
  const columnDef: any = cell.column.columnDef;
  const [value, setValue] = React.useState<{
    value: any;
    extra?: any;
    update: boolean;
  }>({
    value: getValue() || '',
    update: false,
  });

  React.useEffect(() => {
    setValue({ value: getValue(), update: false });
  }, [getValue]);

  React.useEffect(() => {
    if (value.update) {
      dispatch({
        type: 'update_cell',
        columnId: columnDef.accessorKey,
        rowIndex: row.index,
        value: value.value,
        extra: value.extra,
      });
    }
  }, [value, columnDef.id, row.index, dispatch, columnDef]);

  const _disabled = columnDef.disabled || disabled;

  const timeInputRef = React.useRef<HTMLInputElement>(null);

  const timeParser = (value) =>
    value.toLocaleTimeString([], {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    });

  let element;
  switch (columnDef.dataType) {
    case ColumnDataType.text:
    case ColumnDataType.email:
      element = (
        <StyledInput
          ref={ref}
          value={(value.value && value.value.toString()) || ''}
          type={columnDef.dataType === ColumnDataType.text ? 'text' : 'email'}
          onChange={(
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => {
            const result = (() => {
              const fontCase = (columnDef.fontCase ??
                FontCase.Default) as FontCaseType;
              const text = e.target.value;
              switch (fontCase) {
                case 'TitleCase':
                  return toTitleCase(text);
                case 'SentenceCase':
                  return toSentenceCase(text);
                case 'Default':
                default:
                  return text;
              }
            })();
            setValue({ value: result, update: false });
          }}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            onFocus?.(e, row.index, columnIdx);
            e.preventDefault();
            return false;
          }}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            setValue((old) => ({ value: old.value, update: true }));
            onBlur?.(e, row.index, columnIdx);
            e.preventDefault();
            return false;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              return false;
            }
          }}
          disabled={_disabled}
          required={columnDef.required}
        />
      );
      break;

    case ColumnDataType.number:
      element = (
        <StyledNumberFormat
          ref={ref}
          thousandSeparator
          required={columnDef.required}
          value={
            (value.value !== undefined &&
              value.value !== null &&
              value.value.toString()) ||
            ''
          }
          onChange={(
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => {
            const temp = e.currentTarget.value.toString().replace(/,/g, '');
            const val = parseFloat(temp);
            const numberValue = () => {
              if (temp) {
                if (isNaN(val)) {
                  return 0;
                }

                if (Number.isFinite(minNumber) && minNumber! > val) {
                  return minNumber;
                }

                if (Number.isFinite(maxNumber) && maxNumber! < val) {
                  return maxNumber;
                }

                return val;
              }
              return undefined;
            };

            setValue({
              value: numberValue(),
              update: true,
            });
          }}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            onFocus?.(e, row.index, columnIdx);
            e.preventDefault();
            return false;
          }}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            setValue((old) => ({ value: old.value, update: true }));
            onBlur?.(e, row.index, columnIdx);
            e.preventDefault();
            return false;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              return false;
            }
          }}
          disabled={_disabled}
        />
      );
      break;

    case ColumnDataType.select:
    case ColumnDataType.selectMulti:
      element = (
        <TableSelectInput
          {...columnDef.selectProps}
          inputRef={ref}
          required={columnDef.required}
          dataType={columnDef.dataType}
          disabled={_disabled}
          options={columnDef.options || []}
          multi={columnDef.dataType === ColumnDataType.selectMulti}
          selectGetLastItem={columnDef.selectGetLastItem}
          value={value.value}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            onFocus?.(e, row.index, columnIdx);
            e.preventDefault();
            return false;
          }}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            onBlur?.(e, row.index, columnIdx);
            e.preventDefault();
            return false;
          }}
          onChange={(val, extra) => {
            setValue({ value: val, extra, update: true });
          }}
        />
      );
      break;

    case ColumnDataType.delete:
      element = (
        <Action
          tabIndex={0}
          onClick={() => {
            !disabled && dispatch({ type: 'removeRow', rowIndex: row.index });
          }}
          $style={{
            height: '42px',
            width: '60px',
            cursor: disabled ? 'default' : 'pointer',
            ':hover': {
              backgroundColor: disabled
                ? 'transparent'
                : stylesColors.redLightest,
            },
          }}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            onFocus?.(e, row.index, columnIdx);
            e.preventDefault();
            return false;
          }}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            onBlur?.(e, row.index, columnIdx);
            e.preventDefault();
            return false;
          }}
          onKeyDown={(e) => {
            if (e.shiftKey && e.key === 'Enter') {
              e.preventDefault();
            } else if (e.key === 'Enter') {
              !disabled && dispatch({ type: 'removeRow', rowIndex: row.index });
            }
          }}
        >
          <TrashSVG
            color={disabled ? stylesColors.skyDefault : stylesColors.redDefault}
          />
        </Action>
      );
      break;

    case ColumnDataType.date:
      element = (
        <TableDateInput
          ref={ref}
          value={value.value}
          onChange={(value) => {
            setValue({ value, update: true });
          }}
          disabled={_disabled}
          required={columnDef.required}
          placeholder="DD/MM/YYYY"
        />
      );
      break;

    case ColumnDataType.time:
      element = (
        <TimeInput
          ref={timeInputRef}
          value={value.value && timeParser(value.value)}
          rightSection={
            <ActionIcon onClick={() => timeInputRef?.current?.showPicker()}>
              <TriangleDown size={20} color="#000" />
            </ActionIcon>
          }
          onChange={(e) => {
            const time = e.target.value;
            const date = new Date(value.value || Date.now());
            const [hour, minute] = time.split(':').map((v) => parseInt(v, 10));

            date.setHours(hour, minute);

            setValue({ value: date, update: true });
          }}
          disabled={_disabled}
          styles={{
            root: {
              width: '100%',
            },
            input: {
              borderWidth: 0,
              backgroundColor: 'transparent',
              fontSize: '16px',
              opacity: '1 !important',
              color: '#000 !important',
            },
          }}
        />
      );
      break;

    case ColumnDataType.tags:
      element = (
        <>
          {(value.value || [])
            .filter((_, idx) => idx <= (columnDef.maxTags || 4) - 1)
            .map((item) => {
              const label = columnDef.tagKeyName
                ? item[columnDef.tagKeyName]
                : item;
              return (
                <Tag closeable={false} kind={KIND.blue} key={label}>
                  {label}
                </Tag>
              );
            })}
          {(value.value || []).length > (columnDef.maxTags || 4) && (
            <StatefulTooltip
              overrides={{
                Inner: {
                  style: {
                    backgroundColor: 'white',
                  },
                },
                Body: {
                  style: ({ $theme }) => ({
                    zIndex: 10,
                  }),
                },
              }}
              placement={PLACEMENT.bottom}
              content={() => (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 8,
                    maxWidth: 250,
                  }}
                >
                  {(value.value || [])
                    .filter((_, idx) => idx > (columnDef.maxTags || 4) - 1)
                    .map((item) => {
                      const label = columnDef.tagKeyName
                        ? item[columnDef.tagKeyName]
                        : item;
                      return (
                        <Tag closeable={false} kind={KIND.blue} key={label}>
                          {label}
                        </Tag>
                      );
                    })}
                </div>
              )}
            >
              <StyledText>{t('common:more')}</StyledText>
            </StatefulTooltip>
          )}
        </>
      );
      break;

    case ColumnDataType.checkbox:
      element = (
        <StyledCenter>
          <IndeterminateCheckbox
            {...{
              checked: value.value,
              ref,
              onChange: (e) => {
                setValue({ value: e.target.checked, update: true });
                // row.getToggleSelectedHandler()(e);
                e.preventDefault();
              },
              onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                onFocus?.(e, row.index, columnIdx);
                e.preventDefault();
                return false;
              },
              onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                // setValue((old) => ({ value: old.value, update: true }));
                onBlur?.(e, row.index, columnIdx);
                e.preventDefault();
                return false;
              },
              disabled,
            }}
          />
        </StyledCenter>
      );
      break;

    default:
      element = (
        <StyledSpan style={{ display: 'flex', alignItems: 'center' }}>
          <StyledSpan style={{ padding: 0, height: 'auto', fontSize: 16 }}>
            {flexRender(_cell, props.context)}
          </StyledSpan>
        </StyledSpan>
      );
      break;
  }

  return element;
});

export default TableCell;

const StyledNumberFormat = themedStyled(NumberFormat, ({ $theme }) => ({
  border: 'none',
  padding: '8px',
  height: '100%',
  width: '100%',
  backgroundColor: 'transparent',
  textAlign: 'right',
  fontSize: '16px',
  fontFamily,
  ':disabled': {
    color: 'black',
  },
}));

const StyledInput = themedStyled('input', ({ $theme }) => ({
  border: 'none',
  padding: '8px',
  height: '100%',
  width: '100%',
  fontSize: '16px',
  backgroundColor: 'transparent',
  fontFamily,
  ':disabled': {
    color: 'black',
  },
}));

const StyledSpan = themedStyled('span', ({ $theme }) => ({
  padding: '8px',
  width: '100%',
  height: '100%',
  fontFamily,
}));

const StyledText = themedStyled('span', ({ $theme }) => ({
  fontFamily,
  padding: '0 8px',
  margin: '8px',
  display: 'inline-flex',
  color: $theme.colors.blueDark,
  cursor: 'default',
}));

const StyledCenter = themedStyled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 1,
});
